import { ChangeEvent, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { IDocument } from 'kyc-core/dist/Interfaces'
import {
  ALERT_MESSAGES,
  DOCUMENT_TYPE_UPLOAD_FILES_FOR_STEPS,
  FILE_STATUS,
  INVALID,
  STATUS,
} from '../enums/common'
import {
  deleteDocument,
  getDocumentsByTypeIds,
  uploadDocuments,
} from '../services/Kyc'
import {
  IDocInfo,
  IDocumentWithVerificationStatus,
  IFileUploadAlert,
  ISingleFileUploadHookProps,
  IUploadFormState,
  TUpdateFileErrors,
  TUploadAction,
} from '../interfaces/Upload'
import {
  createFileObject,
  getDocumentStatusAlert,
  handleError,
} from '../services/utils'
import { loadingActions } from '../store/slices/Loading'
import { IStore } from '../interfaces/Store'

const useSingleFileUploadHook = (props: ISingleFileUploadHookProps) => {
  const {
    index,
    setState,
    mainState,
    validateFile,
    selectIndex = '',
    isPeriodicRemediation,
    revenueCards = [],
    revenue: currentRevenue,
    handleCardChange,
  } = props
  const fileIndex = props.fileIndex || 0
  const state = mainState[index]
  const breadcrumbStep: number = useSelector(
    (storeState: IStore) => storeState.breadcrumbReducer.step
  )
  const step = breadcrumbStep.toString() + selectIndex

  const folderData = useSelector(
    (storeState: IStore) => storeState.folderReducer.folder
  )
  const revenuesDocumentUids = folderData?.revenue.flatMap(
    (revenue) => revenue.document_uid
  )

  const dispatch = useDispatch()
  const history = useHistory()
  const updateState = useCallback(
    (callBack: (indexPrevStat: IUploadFormState) => IUploadFormState) => {
      setState((prevState: IUploadFormState[]) => {
        if (prevState[index]) {
          const indexPrevState = { ...prevState[index] }
          const newIndexState = callBack(indexPrevState)
          const newState = [...prevState]
          newState[index] = newIndexState
          return newState
        }
        return prevState
      })
    },
    [index, setState]
  )
  const removeExtraDocuments = async (
    documentsList: IDocumentWithVerificationStatus[],
    lastUploadedDocumentUid: string
  ) => {
    try {
      const existingDocumentsUids = revenueCards.flatMap(
        (docList) => docList.document_uid
      )

      // Filter documents that should be kept
      const documentsToKeep = documentsList.filter((doc: IDocument) =>
        [
          ...existingDocumentsUids,
          ...revenuesDocumentUids,
          lastUploadedDocumentUid,
        ].includes(doc.documentUid)
      )
      // Identify the documents to be removed
      const documentsToRemove = documentsList.filter(
        (doc) => !documentsToKeep.includes(doc)
      )

      // Perform delete requests for each document that needs to be removed
      await Promise.all(
        documentsToRemove.map(async (doc) => {
          if (doc.documentUid) {
            try {
              await deleteDocument({ documentUid: doc.documentUid })
            } catch (e) {
              handleError(e, history) // Handle each error separately
            }
          }
        })
      )

      // Return the documents that should be kept
      return documentsToKeep || []
    } catch (e) {
      handleError(e, history)
    }
  }

  const getExistingFiles = useCallback(
    async (
      docsInfo: IDocInfo[],
      validateButton: boolean = true,
      docUid: string = ''
    ) => {
      updateState((prevState) => ({ ...prevState, status: STATUS.pending }))
      try {
        const documents = await getDocumentsByTypeIds([docsInfo[0].typeId])
        const filteredDocuments =
          (await removeExtraDocuments(
            documents as IDocumentWithVerificationStatus[],
            docUid
          )) || []

        const validationButtonError: IFileUploadAlert | null = null

        filteredDocuments.forEach(
          (
            document: IDocumentWithVerificationStatus,
            documentIndex: number
          ) => {
            let file: File | null = null
            let fileAlerts: IFileUploadAlert | null = null
            let fileStatus = FILE_STATUS.pending

            if (document.documentUid) {
              fileAlerts = getDocumentStatusAlert(
                document.status,
                Number(document.documentTypeId),
                Number(document.statutVerification2)
              )
              if (document.status !== INVALID) {
                file = createFileObject([''], document.name)
                fileStatus = FILE_STATUS.loaded
              }
            }
            updateState((prevState) => {
              const files =
                prevState.file.length > documents.length
                  ? [...prevState.file.splice(-1)]
                  : [...prevState.file]

              const filesStatus =
                prevState.fileStatus.length > documents.length
                  ? [...prevState.fileStatus.splice(-1)]
                  : [...prevState.fileStatus]

              const filesAlerts =
                prevState.fileAlerts.length > documents.length
                  ? [...prevState.fileAlerts.splice(-1)]
                  : [...prevState.fileAlerts]

              files[documentIndex] = file
              filesStatus[documentIndex] = fileStatus
              filesAlerts[documentIndex] = fileAlerts

              return {
                ...prevState,
                file: files,
                fileAlerts: filesAlerts,
                status:
                  document.status === 'valid' || document.status === 'pending'
                    ? STATUS.validated
                    : STATUS.loaded,
                fileStatus: filesStatus,
                docInfo: docsInfo,
                validationButtonError,
                toggleValidateButton:
                  document.status === INVALID ? true : validateButton,
              }
            })
          }
        )
      } catch (e) {
        updateState((prevState) => ({
          ...prevState,
          status: STATUS.loaded,
          toggleValidateButton: true,
        }))
        handleError(e, history)
      }
    },
    // eslint-disable-next-line
    [updateState]
  )

  useEffect(() => {
    if (
      folderData?.revenue?.[index]?.document_uid &&
      currentRevenue &&
      currentRevenue.revenu_type !== '2'
    ) {
      dispatch(loadingActions.loading())

      getExistingFiles(
        isPeriodicRemediation
          ? DOCUMENT_TYPE_UPLOAD_FILES_FOR_STEPS[step].periodic_remediation[
              state.docValue
            ]
          : DOCUMENT_TYPE_UPLOAD_FILES_FOR_STEPS[step][
              currentRevenue.document_type
            ],
        false
      ).then(() => {
        dispatch(loadingActions.loaded())

        handleCardChange(index, {
          ...revenueCards[index],
          document_uid: folderData?.revenue?.[index]?.document_uid,
        })
      })
    }
  }, [])
  const handleChange = async (
    event: ChangeEvent<HTMLSelectElement>,
    showValidateButton: boolean = true
  ) => {
    updateState((prevState) => {
      const file = [...prevState.file]
      if (prevState.fileStatus[fileIndex] === FILE_STATUS.pending) {
        file[fileIndex] = null
      }
      return { ...prevState, fileAlerts: [], file }
    })
    const { value } = event.target
    if (!showValidateButton || value === '') {
      updateState((prevState) => ({
        ...prevState,
        toggleValidateButton: false,
      }))
    }
  }

  const updateFiles: TUploadAction = (file) => {
    updateState((prevState) => {
      const files = [...prevState.file]
      const filesStatus = [...prevState.fileStatus]
      const filesAlerts = [...prevState.fileAlerts]
      files[fileIndex] = file
      filesStatus[fileIndex] = FILE_STATUS.pending
      filesAlerts[fileIndex] = null
      return {
        ...prevState,
        file: files,
        fileStatus: filesStatus,
        fileAlerts: filesAlerts,
      }
    })
  }

  const updateFileErrors: TUpdateFileErrors = (error) => {
    updateState((prevState) => {
      const errors = [...prevState.fileAlerts]
      errors[fileIndex] = error
      return { ...prevState, fileAlerts: errors }
    })
  }
  const isAlreadyValidated = () => {
    if (
      state.fileStatus[fileIndex] === FILE_STATUS.loaded &&
      state.docInfo.length === 1
    ) {
      updateState((prevState) => ({
        ...prevState,
        status: STATUS.validated,
        toggleValidateButton: false,
      }))
      return false
    }
    if (
      state.fileStatus.every((fileStatus) => fileStatus === FILE_STATUS.loaded)
    ) {
      updateState((prevState) => ({ ...prevState, status: STATUS.validated }))
      return false
    }
    return true
  }
  const uploadFile = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    identityFiles: boolean = false
  ) => {
    if (!validateFile(index)) {
      return
    }

    if (!isAlreadyValidated()) {
      return
    }

    try {
      dispatch(loadingActions.loading())

      const doc = await uploadDocuments(
        breadcrumbStep,
        state.file as File[],
        isPeriodicRemediation
          ? DOCUMENT_TYPE_UPLOAD_FILES_FOR_STEPS[step].periodic_remediation[
              state.docValue
            ]
          : DOCUMENT_TYPE_UPLOAD_FILES_FOR_STEPS[step][state.docValue],
        identityFiles
      )
      handleCardChange(index, {
        ...revenueCards[index],
        document_uid: [doc[0].documentUid],
      })

      await getExistingFiles(
        isPeriodicRemediation
          ? DOCUMENT_TYPE_UPLOAD_FILES_FOR_STEPS[step].periodic_remediation[
              state.docValue
            ]
          : DOCUMENT_TYPE_UPLOAD_FILES_FOR_STEPS[step][state.docValue],
        false,
        doc[0].documentUid
      )
      updateState((prevState) => ({
        ...prevState,
        status: STATUS.validated,
        toggleValidateButton: false,
      }))
      dispatch(loadingActions.loaded())
    } catch (e) {
      updateState((prevState) => {
        const filesAlerts = [...prevState.fileAlerts]
        filesAlerts[fileIndex] = ALERT_MESSAGES.fileUpload
        const status = STATUS.failed

        return { ...prevState, fileAlerts: filesAlerts, status }
      })
      dispatch(loadingActions.loaded())
      handleError(e, history)
    }
  }

  const resetIndexFileState = () => {
    updateState((prevState) => ({
      ...prevState,
      file: [],
      status: STATUS.idle,
      toggleValidateButton: false,
      fileAlerts: [],
      fileStatus: [],
      validationButtonError: null,
    }))
  }

  return {
    handleChange,
    getExistingFiles,
    uploadFile,
    updateFileErrors,
    updateFiles,
    resetIndexFileState,
    fIndex: fileIndex,
    updateState,
    removeExtraDocuments,
  }
}

export default useSingleFileUploadHook
